/**
 * Client-side paged loader factory for the Inspection Series FilterBar.
 *
 * Converts a static option array (e.g. territoriesForSelect from server props)
 * into a `loadOptions` function compatible with `PagedMultiSelectFilter`.
 *
 * The parent component creates and owns these loaders and passes them into the
 * filter config hook.
 *
 * Usage:
 *   const loadTerritories = createArrayLoader(territoriesForSelect);
 *   // loadTerritories('North', undefined) → Promise<LoadOptionsResult<number>>
 *
 * Note: This mirrors the same utility in InspectionsIndex/adapters/createArrayLoader.ts.
 * It is intentionally duplicated here to avoid cross-feature imports between peer
 * feature directories, consistent with the plan's "adapters remain page-specific" rule.
 */
const PAGE_SIZE = 50;
/**
 * Returns a `loadOptions` callback that filters/paginates through a static array.
 *
 * - Filtering: case-insensitive substring match on `label`.
 * - Pagination: cursor is a stringified offset number.
 * - Returns `nextCursor` when more items exist beyond the current page.
 */
export function createArrayLoader(items, toOption) {
    const defaultToOption = (item) => ({
        value: item.value,
        label: item.label
    });
    const mapItem = toOption !== null && toOption !== void 0 ? toOption : defaultToOption;
    return (query, cursor) => {
        const q = query.trim().toLowerCase();
        const filtered = q ? items.filter((item) => item.label.toLowerCase().includes(q)) : [...items];
        const offset = cursor ? parseInt(cursor, 10) : 0;
        const page = filtered.slice(offset, offset + PAGE_SIZE);
        const hasMore = offset + PAGE_SIZE < filtered.length;
        return Promise.resolve({
            options: page.map(mapItem),
            nextCursor: hasMore ? String(offset + PAGE_SIZE) : undefined
        });
    };
}
